import type { UploadStatus } from "../types";

export const OBJECT_IMPORT_LONG_RUNNING_MS = 20_000;
export const OBJECT_IMPORT_MIN_VISIBLE_STAGE_MS = 300;

export type ObjectImportUiStage =
  | "uploading"
  | "converting"
  | "optimizing"
  | "rendering"
  | "completed";

const STATUS_RANK: Partial<Record<UploadStatus, number>> = {
  uploading: 0,
  uploaded: 0,
  converting: 1,
  optimizing: 2,
  rendering: 3,
  finalizing: 4,
  completed: 5,
};

const STATUS_PROGRESS: Partial<Record<UploadStatus, number>> = {
  uploading: 0,
  uploaded: 0,
  converting: 20,
  optimizing: 40,
  rendering: 60,
  finalizing: 80,
  completed: 100,
};

/**
 * Converter와 Main API의 canonical 40% 진행 상태는 `optimizing`이다.
 * rollout 이전 build가 보내는 `deleting_original`은 수신 시에만 허용하는
 * alias이며, Studio 내부 상태로는 저장하지 않는다.
 */
const CONVERSION_STATUS_ALIASES: Record<string, string> = {
  deleting_original: "optimizing",
};

export const normalizeConversionStatus = (
  status: string | null | undefined,
): string => {
  const normalized = (status ?? "").trim().toLowerCase();
  if (!normalized) return "";
  return CONVERSION_STATUS_ALIASES[normalized] ?? normalized;
};

export const getObjectImportUiStage = (
  status: UploadStatus | undefined,
): ObjectImportUiStage | null => {
  switch (status) {
    case "uploading":
    case "uploaded":
      return "uploading";
    case "converting":
      return "converting";
    case "optimizing":
      return "optimizing";
    case "rendering":
    case "finalizing":
      return "rendering";
    case "completed":
      return "completed";
    default:
      return null;
  }
};

export const getObjectImportStatusRank = (
  status: UploadStatus | undefined,
): number | null => (status == null ? null : STATUS_RANK[status] ?? null);

export const getObjectImportMilestone = (
  status: UploadStatus | undefined,
): number | null => (status == null ? null : STATUS_PROGRESS[status] ?? null);

/**
 * 경고 Import의 TransferPanel 한 줄 문구.
 *
 * FCV 정본(View R UI `3132:380731` 경고 Flow)은 진행 박스를 5칸 채운 상태로
 * 두고 상태 문구만 경고 문구로 바꾼다. 문구에 파일명과 개수를 쓰지 않는다 —
 * 여러 개가 빠지면 한 줄에 담기지 않고, 개수는 Conversion report의 값 칸이
 * 답한다. Loader 재질 불일치 6종은 사용자가 구분할 수 없고 할 일도 같아서 한
 * 문구로 합친다.
 */
const WARNING_STATUS_TEXTS: Array<{ codes: string[]; text: string }> = [
  {
    codes: ["DEFAULT_MATERIAL_APPLIED"],
    text: "No material found - default gray applied",
  },
  {
    codes: [
      "LOADER_MATERIAL_MAPPING_FAILED",
      "LOADER_MATERIAL_SLOT_MISMATCH",
      "LOADER_PBR_MAPPING_MISMATCH",
      "LOADER_TEXTURE_MAPPING_MISMATCH",
      "LOADER_UV_MAPPING_MISMATCH",
      "LOADER_DOUBLE_SIDED_MISMATCH",
    ],
    text: "Some materials may look different",
  },
  {
    codes: ["EXTERNAL_TEXTURE_MISSING"],
    text: "Some textures are missing",
  },
];

/**
 * 여러 경고가 겹치면 Conversion report의 행 순서(Materials → Textures)를 따라
 * 첫 문구 하나만 쓴다. 등록되지 않은 경고 코드는 null을 돌려주고 호출부가
 * 기존 완료 문구를 유지한다.
 */
export const getObjectImportWarningStatusText = (
  eventCodes: readonly string[] | undefined,
): string | null => {
  if (!eventCodes?.length) return null;
  const codes = new Set(eventCodes);
  for (const entry of WARNING_STATUS_TEXTS) {
    if (entry.codes.some((code) => codes.has(code))) return entry.text;
  }
  return null;
};
